import { defineStore } from 'pinia'
import { fetchHomeData } from '../services/api'
import { useCartStore } from './cartStore.js'
import { useAuthStore } from "@/stores/auth.js";
import { slugify } from '@/utils/slugify'



export const useDataStore = defineStore('data', {
    state: () => ({
        categories: [],
        subcategories: [],
        ads: [],
        configurations: [],
        articles: [],
        physicalProducts: [],
        favoris: [],
        user: {
            id: null,
            name: 'Guest',
            is_da: 0,
            hide_price: 0,
            wallet: 0,
            wallet_da: 0,
            wallet_euro: 0,
            wallet_egp: 0,
            is_active: 0,
        },
        loading: false,
        error: null,
        lastFetched: null,
        isInitialized: false,
        shouldUseDAPricing: true,
        country: null,


    }),

    getters: {
        // Add this getter to check if data is ready
        isDataReady: (state) => {
            return state.isInitialized &&
                state.categories.length > 0 &&
                !state.loading
        },

        getCategory: (state) => (id) => {
            return state.categories.find(cat => cat.id === parseInt(id)) || null
        },

        getSubcategoriesByCategoryId: (state) => (categoryId) => {
            return state.subcategories.filter(sub => sub.categorie_id.toString() === categoryId.toString())
        },

        getArticlesBySubcategoryId: (state) => (subcategoryId) => {
            // First check if articles exists and is an array
            if (!Array.isArray(state.articles)) return [];


            return state.articles.filter(article =>
                parseInt(article?.sous_categorie_id) === parseInt(subcategoryId)
            );
        },

        getConfigValue: (state) => (key, defaultValue = null) => {
            const config = state.configurations.find(c => c.key === key)
            return config ? config.value : defaultValue
        },

        currencySymbol: (state) => {
            if (state.shouldUseDAPricing) return 'DA';
            switch (state.user.is_da.toString()) {
                case "1":
                    return '€'
                case "2":
                    return 'DA'
                case "3":
                    return 'EGP'
                default:
                    return '$'
            }
        },

        currencyRate: (state) => {
            switch (state.user.is_da.toString()) {
                case "1":
                    return parseFloat(state.getConfigValue('changeeuro', 0.88))
                case "2":
                    return parseFloat(state.getConfigValue('changeDa', 260))
                case "3":
                    return parseFloat(state.getConfigValue('changeegp', 55))
                default:
                    return 1
            }
        },

        userWallet: (state) => {
            switch (state.user.is_da.toString()) {
                case "1":
                    return state.user.wallet_euro || 0
                case "2":
                    return state.user.wallet_da || 0
                case "3":
                    return state.user.wallet_egp || 0
                default:
                    return state.user.wallet || 0
            }
        },

        formattedWallet: (state) => {
            return `${state.currencySymbol} ${state.userWallet.toFixed(2)}`
        },

        shouldHidePrices: (state) => {
            return state.user.hide_price === 1
        },

        shouldUseDAPricingGetter: (state) => {
            return state.user.shouldUseDAPricing
        },

        userName: (state) => {
            return state.user.name
        },

        shouldHideCart: (state) => {
            const authStore = useAuthStore();
            // Basket/cart is always available for unlogged guests
            if (!authStore.isLoggedIn) return false;
            // Basket/cart is always available for inactive accounts
            const user = authStore.user || state.user;
            const isActive = user?.is_active === 1 || user?.is_active === '1';
            if (!isActive) return false;
            // Only hide cart for verified active wholesale accounts in Algeria
            return state.country === 'DZ' || user?.country === 'DZ' || user?.is_da === 2 || user?.is_da === '2';
        },

        shouldHideOrderNow: (state) => {

            return state.country === 'EG';
        },

        shouldHideWallet: (state) => {

            return state.country === 'EG';
        },


    },

    actions: {
        async fetchData(forceRefresh = false) {
            // Prevent multiple simultaneous calls
            if (this.loading && !forceRefresh) {
                // Wait for current fetch to complete
                while (this.loading) {
                    await new Promise(resolve => setTimeout(resolve, 100))
                }
                return
            }

            // If data exists and was fetched recently, return early unless forced
            const fiveMinutes = 5 * 60 * 1000
            if (
                !forceRefresh &&
                this.isInitialized &&
                this.lastFetched &&
                this.categories.length > 0 &&
                Date.now() - this.lastFetched < fiveMinutes
            ) {
                // Using cached data
                return
            }

            // Fetching fresh data...
            this.loading = true
            this.error = null

            try {
                const authStore = useAuthStore()
                const data = await fetchHomeData()

                this.categories = data.categories || []
                this.subcategories = data.scategories || []
                this.articles = data.articles || []
                this.physicalProducts = data.physical_products || []
                this.ads = data.ads || []
                this.configurations = data.configurations || []
                this.shouldUseDAPricing = data.shouldUseDAPricing ?? true
                this.lastFetched = Date.now()
                this.isInitialized = true
                this.country = data.country || null
                this.favoris = data.favoris || []

                // Check if user is authenticated (both in authStore and in API response)
                const isAuthenticated = authStore.isLoggedIn && data.user?.id != null

                if (isAuthenticated) {
                    // User is authenticated - update with real user data
                    this.user = {
                        id: data.user.id,
                        name: data.user.name || 'User',
                        is_da: data.user.is_da ?? 0,
                        hide_price: data.user.hide_price ?? 0,
                        wallet: parseFloat(data.user.wallet) || 0,
                        wallet_da: parseFloat(data.user.wallet_da) || 0,
                        wallet_euro: parseFloat(data.user.wallet_euro) || 0,
                        wallet_egp: parseFloat(data.user.wallet_egp) || 0,
                        is_active: data.user.is_active ?? 1,
                    }

                    // Update currency preference
                    this.updateUserCurrency(data.user.is_da)

                    // Update auth store with latest wallet data
                    authStore.updateUserWallet(data.user)
                } else {
                    // User is NOT authenticated - set guest user based on country
                    this.user = {
                        id: null,
                        name: 'Guest',
                        is_da: data.country === 'DZ' ? 2 : data.country === 'europe' ? 1 : data.country === 'EG' ? 3 : 0,
                        hide_price: 0,
                        wallet: 0,
                        wallet_da: 0,
                        wallet_euro: 0,
                        wallet_egp: 0,
                        is_active: 0,
                    }
                }

                // Update cart prices with latest currency settings
                const cartStore = useCartStore()
                cartStore.items.forEach(item => {
                    const rawId = item.originalId || (typeof item.id === 'string' ? item.id.split('-')[0] : item.id)
                    const article = this.articles.find(a => a.id?.toString() === rawId?.toString() || a.id?.toString() === item.id?.toString())
                    if (article && !item.is_custom_price) {
                        const converted = parseFloat(this.convertPrice(article.prix || 0, article))
                        item.price = !isNaN(converted) && converted > 0 ? converted : (parseFloat(article.prix) || 0)
                        item.originalPrice = item.price
                        if (!item.originalId) {
                            item.originalId = article.id
                        }
                    }
                })
                cartStore.recalculatePrices()

            } catch (error) {
                console.error('Error fetching data:', error)
                this.error = 'Failed to load data. Please try again.'
                throw error // Re-throw so router can handle it
            } finally {
                this.loading = false
            }
        },

        // ... rest of your existing actions remain the same
        updateWalletBalance(newBalance) {
            if (!this.user) return

            const walletType = this.user.is_da.toString()

            switch (walletType) {
                case "1":
                    this.user.wallet_euro = newBalance
                    break
                case "2":
                    this.user.wallet_da = newBalance
                    break
                case "3":
                    this.user.wallet_egp = newBalance
                    break
                default:
                    this.user.wallet = newBalance
            }
        },

        setUserData(userData) {
            if (!userData) {
                this.user = {
                    id: null,
                    name: 'Guest',
                    is_da: 0,
                    hide_price: 0,
                    wallet: 0,
                    wallet_da: 0,
                    wallet_euro: 0,
                    wallet_egp: 0,
                    is_active: 0,

                }
                return
            }

            this.user = {
                id: userData.id || null,
                name: userData.name || 'Guest',
                is_da: userData.is_da || 0,
                hide_price: userData.hide_price || 0,
                wallet: userData.wallet || 0,
                wallet_da: userData.wallet_da || 0,
                wallet_euro: userData.wallet_euro || 0,
                wallet_egp: userData.wallet_egp || 0,
                is_active: userData.is_active || 0,
            }
        },

        setUserDisplayDa(abr) {
            this.shouldUseDAPricing = abr;
        },

        convertPrice(price, article = null) {
            const num = parseFloat(price);
            if (isNaN(num)) {
                return '0.00';
            }
            if (num === 0 || num === -1) {
                return num.toFixed(2);
            }

            // Determine active target currency
            const isDACurrency = this.shouldUseDAPricing || this.currencySymbol === 'DA' || this.user?.is_da === 2 || this.user?.is_da === "2";
            const isEURCurrency = this.currencySymbol === '€' || this.user?.is_da === 1 || this.user?.is_da === "1";
            const isEGPCurrency = this.currencySymbol === 'EGP' || this.user?.is_da === 3 || this.user?.is_da === "3";
            const isUSDCurrency = !isDACurrency && !isEURCurrency && !isEGPCurrency;

            const rateDa = parseFloat(this.getConfigValue('changeDa', 260)) || 260;
            const rateEur = parseFloat(this.getConfigValue('changeeuro', 0.88)) || 0.88;
            const rateEgp = parseFloat(this.getConfigValue('changeegp', 50)) || 50;

            // Check if article is a flexy offer (which is ALWAYS inherently priced in DA)
            const isFlexyOffer = article && (
                article.operator ||
                article.type === 'activated' ||
                article.type === 'direct' ||
                (typeof article.id === 'string' && (
                    article.id.includes('MIX') ||
                    article.id.includes('TALK') ||
                    article.id.includes('NET') ||
                    article.id.includes('DJEZZY') ||
                    article.id.includes('MOBILIS') ||
                    article.id.includes('OOREDOO')
                )) ||
                (typeof article.flexi_code === 'string' && (
                    article.flexi_code.toUpperCase().startsWith('ADSL') ||
                    article.flexi_code.toUpperCase().startsWith('4G')
                ))
            );

            // Resolve target article from this.articles if not passed
            let targetArticle = article;
            if (!targetArticle && Array.isArray(this.articles)) {
                targetArticle = this.articles.find(a => 
                    parseFloat(a.prix_da) === num || 
                    parseFloat(a.prix) === num ||
                    parseFloat(a.original_prix) === num ||
                    (a.is_fixed_da && parseFloat(a.prix) === num)
                ) || null;
            }

            // Check if user is verified active wholesale user
            const authStore = useAuthStore();
            const user = authStore.user || this.user;
            const isActiveWholesale = (user?.is_active === 1 || user?.is_active === '1') && !this.shouldUseDAPricing;

            // Check if price is already in DA
            const isAlreadyDA = isFlexyOffer || 
                (targetArticle && targetArticle.is_fixed_da) ||
                (!isActiveWholesale && targetArticle && targetArticle.prix_da && Math.abs(num - parseFloat(targetArticle.prix_da)) < 0.01);

            // --- CASE 1: Target currency is Algerian Dinars (DA) ---
            if (isDACurrency) {
                // If the price is already in DA, return directly without multiplying
                if (isAlreadyDA) {
                    return num.toFixed(2);
                }

                // If non-active/retail pricing applies (guest or inactive account in DZ):
                if (!isActiveWholesale && targetArticle && targetArticle.prix_da && parseFloat(targetArticle.prix_da) > 0) {
                    const prixDa = parseFloat(targetArticle.prix_da);
                    const basePrix = parseFloat(targetArticle.original_prix || targetArticle.prix || 0);

                    // If num matches USD base price, return exact DA price
                    if (basePrix > 0 && Math.abs(num - basePrix) < 0.01) {
                        return prixDa.toFixed(2);
                    }
                    // If num is multiple of base price (quantity * price), scale DA price
                    if (basePrix > 0 && num > 0) {
                        const qty = num / basePrix;
                        if (Math.abs(qty - Math.round(qty)) < 0.05) {
                            return (prixDa * Math.round(qty)).toFixed(2);
                        }
                    }
                    return prixDa.toFixed(2);
                }

                // If active user and num was passed as retail prix_da, convert from base USD instead:
                if (isActiveWholesale && targetArticle && targetArticle.original_prix && targetArticle.prix_da && Math.abs(num - parseFloat(targetArticle.prix_da)) < 0.01) {
                    const baseUsd = parseFloat(targetArticle.original_prix);
                    const rate = parseFloat(this.currencyRate) || rateDa;
                    return (baseUsd * rate).toFixed(2);
                }

                // If store is globally in DA mode and no specific rule triggered
                if (this.shouldUseDAPricing) {
                    return num.toFixed(2);
                }

                // Convert base USD to DA via active wholesale exchange rate
                const rate = parseFloat(this.currencyRate) || rateDa;
                return (num * rate).toFixed(2);
            }

            // --- CASE 2: Target currency is USD ($) ---
            if (isUSDCurrency) {
                // If article has original USD price, use it
                if (targetArticle && targetArticle.original_prix && parseFloat(targetArticle.original_prix) > 0) {
                    const baseUsd = parseFloat(targetArticle.original_prix);
                    const prixDa = parseFloat(targetArticle.prix_da || 0);
                    if (prixDa > 0 && Math.abs(num - prixDa) < 0.01) {
                        return baseUsd.toFixed(2);
                    }
                    if (baseUsd > 0 && Math.abs(num - baseUsd) < 0.01) {
                        return baseUsd.toFixed(2);
                    }
                    if (prixDa > 0 && num > 0) {
                        const qty = num / prixDa;
                        if (Math.abs(qty - Math.round(qty)) < 0.05) {
                            return (baseUsd * Math.round(qty)).toFixed(2);
                        }
                    }
                }

                // If price was passed in DA, convert DA to USD
                if (isAlreadyDA) {
                    return (num / rateDa).toFixed(2);
                }

                return num.toFixed(2);
            }

            // --- CASE 3: Target currency is EUR (€) or EGP ---
            const targetRate = isEURCurrency ? rateEur : (isEGPCurrency ? rateEgp : (parseFloat(this.currencyRate) || 1));

            if (targetArticle && targetArticle.original_prix && parseFloat(targetArticle.original_prix) > 0) {
                const baseUsd = parseFloat(targetArticle.original_prix);
                const prixDa = parseFloat(targetArticle.prix_da || 0);
                let qty = 1;
                if (prixDa > 0 && Math.abs(num - prixDa) < 0.01) {
                    qty = 1;
                } else if (baseUsd > 0 && Math.abs(num - baseUsd) < 0.01) {
                    qty = 1;
                } else if (prixDa > 0 && num > 0) {
                    const q = num / prixDa;
                    if (Math.abs(q - Math.round(q)) < 0.05) {
                        qty = Math.round(q);
                    }
                }
                return ((baseUsd * qty) * targetRate).toFixed(2);
            }

            if (isAlreadyDA) {
                const usdVal = num / rateDa;
                return (usdVal * targetRate).toFixed(2);
            }

            return (num * targetRate).toFixed(2);
        },

        getSubCategoryById(id) {
            return this.subcategories.find(sub => sub.id === parseInt(id))
        },

        updateUserCurrency(newIsDA) {
            // Validate the input - is_da should be 0, 1, 2, or 3
            const validValues = [0, 1, 2, 3];
            if (!validValues.includes(parseInt(newIsDA))) {
                console.error('Invalid is_da value. Must be 0 (USD), 1 (EUR), 2 (DZD), or 3 (EGP)');
                return false;
            }

            // Update the user.is_da value
            this.user.is_da = parseInt(newIsDA);

            // Optionally persist to localStorage if you're using it
            if (typeof localStorage !== 'undefined') {
                const userData = JSON.parse(localStorage.getItem('user') || '{}');
                userData.is_da = parseInt(newIsDA);
                localStorage.setItem('user', JSON.stringify(userData));
            }

            return true;
        },

        getCategoryBySlug(categorySlug) {

            // Slugify function (same as before)
            const slugify = (text) => {
                if (!text) return '';
                return text
                    .toString()
                    .toLowerCase()
                    .trim()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/[^\w\-]+/g, '')
                    .replace(/\-\-+/g, '-')
                    .replace(/^-+/, '')
                    .replace(/-+$/, '');
            };

            // Find category by comparing slugified libelle
            const category = this.categories.find(cat => {
                const catSlug = slugify(cat.libelle);
                return catSlug === categorySlug;
            });
            return category;
        },

        // UPDATED: Get subcategory by slug path (as an action)
        getSubcategoryBySlugPath(categorySlug, subcategorySlug) {

            // First find the category
            const category = this.getCategoryBySlug(categorySlug);
            if (!category) {
                return null;
            }

            // Then find subcategory that matches both category and slug
            const subcategory = this.subcategories.find(subcat => {
                const subcatSlug = slugify(subcat.libelle);
                const matchesSlug = subcatSlug === subcategorySlug;
                const matchesCategory = subcat.categorie_id.toString() === category.id.toString();

                return matchesSlug && matchesCategory;
            });

            return subcategory;
        },

        // Keep these as they are (they work fine)
        getCategorySlugPath(categoryId) {
            const slugify = (text) => {
                if (!text) return '';
                return text
                    .toString()
                    .toLowerCase()
                    .trim()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/[^\w\-]+/g, '')
                    .replace(/\-\-+/g, '-')
                    .replace(/^-+/, '')
                    .replace(/-+$/, '');
            };

            const category = this.categories.find(c => c.id === parseInt(categoryId));
            if (!category) return null;

            return `/${slugify(category.libelle)}`;
        },

        getSubcategorySlugPath(subcategoryId) {
            const slugify = (text) => {
                if (!text) return '';
                return text
                    .toString()
                    .toLowerCase()
                    .trim()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/[^\w\-]+/g, '')
                    .replace(/\-\-+/g, '-')
                    .replace(/^-+/, '')
                    .replace(/-+$/, '');
            };

            const subcat = this.subcategories.find(s => s.id === parseInt(subcategoryId));
            if (!subcat) return null;

            const category = this.categories.find(cat => cat.id === subcat.categorie_id);
            if (!category) return null;

            return `/${slugify(category.libelle)}/${slugify(subcat.libelle)}`;
        },

    }
})